import React, { useEffect, useRef } from "react";
import MyRouter from "routers/index";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { getAccessToken, getUserId } from "services/cookieService";
import {
  fetchCart,
  selectIsCartLoaded,
  setCartDataRemoved,
} from "store/slices/cartSlice";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import toast from "react-hot-toast";
import {
  selectAuthLoading,
  selectIsAuthLoaded,
  selectUser,
} from "store/slices/authSlice";
import { fetchUserById } from "store/slices/userSlice";
import {
  fetchUserMockups,
  selectUserMockupsLoaded,
} from "store/slices/mockupSlice";
import {
  fetchUserCatalogs,
  selectUserCatalogsLoaded,
} from "store/slices/catalogSlice";
import { selectIsUserTerminated } from "store/slices/uiSlice";
import { extractErrorMessage } from "utils/ErrorHandlers";
import dataRemovalNotifyService from "services/dataRemovalNotifyService";
import socket from "utils/socket";
import GlobalDialog from "shared/GlobalDialog/GlobalDialog";
import { handleOnProductChange } from "utils/socketHandlers";
import { store } from "store/Store";
import { validateLocalCartOnStartup } from "utils/cartValidator";
import { fetchProducts, removeProduct } from "store/slices/productsSlice";
import catalogService from "services/catalogService";
import { fetchLiveEvents } from "store/slices/eventsSlice";

function App() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isAuthLoading = useAppSelector(selectAuthLoading);
  const isAuthLoaded = useAppSelector(selectIsAuthLoaded);
  const isCartLoaded = useAppSelector(selectIsCartLoaded);
  const isMockupsLoaded = useAppSelector(selectUserMockupsLoaded);
  const isCatalogsLoaded = useAppSelector(selectUserCatalogsLoaded);
  const isLoggingOut = useAppSelector(selectIsUserTerminated);

  const userId = getUserId();
  const token = getAccessToken();

  // Prevent double fetch in React 18 Strict Mode
  const hasFetchedMockups = useRef(false);
  const hasFetchedCatalogs = useRef(false);

  // ===================== AUTH + USER FETCH =====================
  useEffect(() => {
    if (!token || !userId || isAuthLoaded) return;

    dispatch(fetchUserById()).catch((err) =>
      toast.error(extractErrorMessage(err) || "Failed to fetch user")
    );
  }, [dispatch, token, userId, isAuthLoaded]);

  // ===================== CART FETCH =====================
  useEffect(() => {
    if (!user?.id || isCartLoaded || isLoggingOut) return;

    dispatch(fetchCart({ userId: user.id, isAlertRequired: true })).catch(
      (err) => toast.error(extractErrorMessage(err))
    );
  }, [dispatch, user?.id, isCartLoaded, isLoggingOut]);

  // ===================== MOCKUPS & CATALOGS =====================
  useEffect(() => {
    if (!user?.id || isLoggingOut) return;

    if (!isMockupsLoaded) {
      hasFetchedMockups.current = false;
    }
    if (!isCatalogsLoaded) {
      hasFetchedCatalogs.current = false;
    }

    if (!isMockupsLoaded && !hasFetchedMockups.current) {
      dispatch(fetchUserMockups()).catch((err) =>
        toast.error(extractErrorMessage(err) || "Failed to fetch mockups")
      );
      // hasFetchedMockups.current = true;
    }

    if (!isCatalogsLoaded && !hasFetchedCatalogs.current) {
      dispatch(fetchUserCatalogs()).catch((err) =>
        toast.error(extractErrorMessage(err) || "Failed to fetch catalogs")
      );
      // hasFetchedCatalogs.current = true;
    }

    // Check removed cart data
    if (isCartLoaded) {
      dataRemovalNotifyService
        .getUserNotificationStatus(user.id)
        .then((res) => {
          if (res.data.cart) dispatch(setCartDataRemoved(true));
        })
        .catch((err) =>
          toast.error(
            extractErrorMessage(err) || "Failed to fetch notification status"
          )
        );
    }
  }, [
    dispatch,
    user?.id,
    isCartLoaded,
    isMockupsLoaded,
    isCatalogsLoaded,
    isLoggingOut,
  ]);

  // ===================== SOCKET CONNECTION =====================
  useEffect(() => {
    if (!isAuthLoaded || !userId) return;

    socket.emit("authenticate", { userId, token });

    const onWelcome = (msg: string) => console.info(msg);
    const onCartUpdated = () =>
      dispatch(fetchCart({ userId, isAlertRequired: true }));

    socket.on("welcome", onWelcome);
    socket.on("cartUpdated", onCartUpdated);

    return () => {
      socket.off("welcome", onWelcome);
      socket.off("cartUpdated", onCartUpdated);
    };
  }, [isAuthLoaded, userId, token, dispatch]);

  useEffect(() => {
    const onCatalogGenerated = async (payload: {
      catalogId: string;
      deviceFingerprint: string;
    }) => {
      let pending = JSON.parse(
        localStorage.getItem("pendingCatalogFingerprints") || "[]"
      );
      const index = pending.indexOf(payload.deviceFingerprint);

      if (index !== -1) {
        try {
          // Step 1: get presigned S3 URL
          const url = await catalogService.downloadCatalog(payload.catalogId);

          // Step 2: fetch the file as blob
          const response = await fetch(url);
          const blob = await response.blob();

          // Step 3: create a temporary object URL
          const objectUrl = window.URL.createObjectURL(blob);

          // Step 4: trigger download
          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = `catalog-${payload.catalogId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Step 5: cleanup
          window.URL.revokeObjectURL(objectUrl);

          toast.success("Catalog generated and downloaded successfully!");

          // Remove from pending
          pending.splice(index, 1);
          localStorage.setItem(
            "pendingCatalogFingerprints",
            JSON.stringify(pending)
          );

          // Refresh user catalogs
          dispatch(fetchUserCatalogs());
        } catch (err) {
          console.error("Download failed:", err);
          toast.error("Failed to download catalog.");
        }
      }
    };

    socket.on("onGenerateCatalog", onCatalogGenerated);

    return () => {
      socket.off("onGenerateCatalog", onCatalogGenerated);
    };
  }, [dispatch]);

  // ===================== PRODUCT CHANGE SOCKET =====================
  useEffect(() => {
    const onProductChange = (payload: any) => {
      handleOnProductChange(dispatch, store.getState, payload);

      // Handle both legacy array format and new object format
      const products = Array.isArray(payload)
        ? payload
        : payload?.products || [];

      products.forEach((item: any) => {
        if (item.product?.variants?.length === 0) {
          dispatch(removeProduct(item.product.id));
        }
      });

      const params = new URLSearchParams(window.location.search);
      const filters = {
        page: 1,
        pageSize: 20,
        category: params.getAll("category")[0] || undefined,
        subCategory: params.get("subCategory") || undefined,
        brand: params.getAll("brand")[0] || undefined,
        searchQuery: params.get("searchQuery") || undefined,
        minPrice: Number(params.get("minPrice")) || undefined,
        maxPrice: Number(params.get("maxPrice")) || undefined,
        ...(params.get("rate") && {
          sortBy: "rate",
          sortOrder: params.get("rate") as "ASC" | "DESC",
        }),
      };

      dispatch(fetchProducts(filters));
    };

    socket.on("onProductChange", onProductChange);

    return () => {
      socket.off("onProductChange", onProductChange);
    };
  }, [dispatch]);

  // ===================== LOCAL CART VALIDATION =====================
  useEffect(() => {
    if (!userId) validateLocalCartOnStartup();
  }, [userId]);

  // ===================== LIVE EVENTS FETCH =====================
  useEffect(() => {
    dispatch(fetchLiveEvents());
  }, [dispatch]);

  if (isAuthLoading && window.location.search.includes("code")) {
    return <div>Loading...</div>;
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>360CUSTOMIZER</title>
        <meta
          name="description"
          content="Best custom printed products for gifting and corporate needs. Custom printed Tshirts, Uniforms, Gadgets etc."
        />
      </Helmet>
      <div className="bg-white text-base dark:bg-slate-900 text-slate-900 dark:text-slate-200">
        <MyRouter />
        <GlobalDialog />
      </div>
    </HelmetProvider>
  );
}

export default App;
